import React, { Fragment } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import AiSparkleIcon from '@atlaskit/icon/core/ai-sparkle';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';

import { ShortcutsSection } from '@confluence/space-shortcuts/entry-points/shortcutsSection.ts';
import { SpaceViewsController } from '@confluence/space-views';
import { PageTreeLoaderOnHover } from '@confluence/page-tree/entry-points/PageTreeLoader.tsx';
import { BlogTree } from '@confluence/blog-tree/entry-points/BlogTree.tsx';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { LoadableAfterPaint } from '@confluence/loadable';
import {
	SPACE_CONTRIBUTORS,
	SPACE_SUGGESTIONS,
} from '@confluence/named-routes/entry-points/routes.ts';
import { useIsOnRoute } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { hasSpaceOperation, MANAGE_CONTENT } from '@confluence/space-utils/entry-points/graphql.ts';

const AgentContextItem = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-AgentContextItem" */ '@confluence/admin-side-navigation/entry-points/AgentContextItem.ts'
			)
		).AgentContextItem,
});

const i18n = defineMessages({
	suggestionsHeader: {
		id: 'side-navigation.space-navigation.suggestions.header',
		defaultMessage: 'Suggestions',
		description:
			'The text shown as a section heading for the Suggestions tab in the Confluence space sidebar.',
	},
	contributorsHeader: {
		id: 'side-navigation.space-navigation.contributors.header',
		defaultMessage: 'Contributors',
		description:
			'The text shown as a link in the Confluence space sidebar to navigate to the space contributors page.',
	},
});

export const CommonSpaceNavigationContent = ({
	showShortcutsSection,
	space,
	spaceKey,
	quick,
	homepageId,
	loading,
	isSpaceAdmin,
	isBlogNavigation,
	blogsHidden,
	pageTreeFinishedLoading,
	onDragDropSuccess,
	onContentTreeLoadComplete,
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { isAdminHubAIEnabled, isAIEarlyAccessEnabled } = useSessionData();
	// Derive from the SSR-preloaded SpaceNavigationQuery's `space.operations` (passed via the `space`
	// prop) instead of firing a separate client-only query, so the Suggestions link can render during
	// SSR and on first client render without a hydration mismatch.
	const canManageContent = hasSpaceOperation(space?.operations, MANAGE_CONTENT);
	const isContributorsSelected = useIsOnRoute(SPACE_CONTRIBUTORS);
	const isSuggestionsSelected = useIsOnRoute(SPACE_SUGGESTIONS);

	return (
		<>
			{expVal('confluence_space_contributors', 'isEnabled', false) && (
				<LinkMenuItem
					href={SPACE_CONTRIBUTORS.toUrl({ spaceKey })}
					elemBefore={<PeopleGroupIcon label="" color="currentColor" />}
					data-testid="space-contributors-link"
					isSelected={isContributorsSelected}
				>
					{formatMessage(i18n.contributorsHeader)}
				</LinkMenuItem>
			)}
			{canManageContent &&
				isAIEarlyAccessEnabled &&
				expVal('confluence_space_organizer', 'isEnabled', false) && (
					<LinkMenuItem
						href={SPACE_SUGGESTIONS.toUrl({ spaceKey })}
						elemBefore={<AiSparkleIcon label="" color="currentColor" />}
						data-testid="space-suggestions-link"
						isSelected={isSuggestionsSelected}
					>
						{formatMessage(i18n.suggestionsHeader)}
					</LinkMenuItem>
				)}
			{showShortcutsSection && (
				<ShortcutsSection
					isSpaceAdmin={isSpaceAdmin}
					spaceKey={spaceKey}
					links={quick}
					spaceId={space?.id || null}
				/>
			)}

			{!loading ? (
				<div
					data-vc="pageTree"
					data-testid="pageTree"
					onMouseEnter={
						expVal('confluence_frontend_remove_hydrateonhover_pagetree', 'isEnabled', false)
							? undefined
							: PageTreeLoaderOnHover.hydrateOnHover
					}
				>
					<SpaceViewsController
						key={spaceKey} // Remount on space change to reset Space views state
						homepageId={homepageId}
						onDragDropSuccess={onDragDropSuccess}
						isPeekingFromBlogs={isBlogNavigation}
						onContentTreeLoadComplete={onContentTreeLoadComplete}
						spaceHasUserContent={space?.didContainUserContent}
						pageTreeFinishedLoading={pageTreeFinishedLoading}
					/>
				</div>
			) : (
				<Fragment />
			)}

			{isAdminHubAIEnabled &&
			isAIEarlyAccessEnabled &&
			expValEquals('cc_space_agents_md_context', 'isEnabled', true) ? (
				<TransparentErrorBoundary attribution={Attribution.MIGRATIONS_TOOLING}>
					<AgentContextItem spaceKey={spaceKey} />
				</TransparentErrorBoundary>
			) : null}

			{!loading && !blogsHidden && (
				<BlogTree spaceKey={spaceKey} pageTreeFinishedLoading={pageTreeFinishedLoading} />
			)}
		</>
	);
};
